// "Areas worked" region cards — realistic anatomy thumbnails replacing the
// abstract 4-panel diagram grid. Tapping a card reveals that region's detailed
// diagram (worked areas highlighted) below the grid.
import { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import BodyDiagram from "./BodyDiagram";

const AREA_CARDS = [
  { key: "head_front", title: "Head & Face —", subtitle: "Anterior", img: "/anatomy/cards/head_front.png" },
  { key: "head_back", title: "Head & Neck —", subtitle: "Posterior", img: "/anatomy/cards/head_back.png" },
  { key: "front", title: "Anterior —", subtitle: "Full Body", img: "/anatomy/cards/front.png" },
  { key: "back", title: "Posterior —", subtitle: "Full Body", img: "/anatomy/cards/back.png" },
];

export const AreasWorkedCards = ({ workedAreas = [], readOnly = true, onChange, diagramProps = {} }) => {
  const [open, setOpen] = useState(null);
  const openCard = AREA_CARDS.find((c) => c.key === open);

  return (
    <div data-testid="areas-worked-cards">
      <p className="text-ink-500 mb-5">Tap an area to review details or make changes.</p>

      <div className="grid grid-cols-2 gap-4 md:gap-6">
        {AREA_CARDS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setOpen(open === c.key ? null : c.key)}
            data-testid={`area-card-${c.key}`}
            className={`relative group text-left bg-paper-50 border rounded-3xl p-4 sm:p-6 pb-14 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all ${
              open === c.key ? "border-sage-500/60 ring-2 ring-sage-500/20" : "border-paper-200"
            }`}
          >
            <div className="font-heading text-base sm:text-lg font-medium text-ink-900 text-center leading-snug mb-3">
              {c.title}
              <br />
              {c.subtitle}
            </div>
            <img
              src={c.img}
              alt={`${c.title} ${c.subtitle}`}
              className="h-40 sm:h-52 w-auto mx-auto object-contain"
              loading="lazy"
              draggable={false}
            />
            <span
              className={`absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center transition-all ${
                open === c.key ? "bg-sage-500 text-white rotate-90" : "text-ink-700 group-hover:text-sage-700"
              }`}
            >
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </span>
          </button>
        ))}
      </div>

      {openCard && (
        <div className="mt-6 bg-white border border-paper-200 rounded-3xl p-5 sm:p-7 animate-fade-up" data-testid={`area-detail-${openCard.key}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="font-heading text-lg font-medium text-ink-900">
              {openCard.title} {openCard.subtitle}
            </div>
            <button
              type="button"
              onClick={() => setOpen(null)}
              data-testid="area-detail-close"
              className="w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <BodyDiagram
            selected={workedAreas}
            aiFocus={[]}
            readOnly={readOnly}
            onChange={onChange}
            regions={[openCard.key]}
            {...diagramProps}
          />
        </div>
      )}
    </div>
  );
};
